/**
 * Upload Center: one queue for every upload in the app, owned above the router.
 *
 * Why this exists. Uploads used to live inside the upload dialog, so the dialog had
 * to stay open and on that creative until the last byte landed. A shoot is 14 GB
 * across several creatives, so that meant one creative at a time and a window
 * nobody could touch. Moving the queue above the router means the dialog is just a
 * way to ADD work: close it, walk to another creative, queue more, and the transfers
 * carry on.
 *
 * Why one pool and not one per creative. Three parallel transfers per creative
 * across four creatives is twelve streams fighting for the same uplink, which is how
 * you get timeouts and half-written files. There is exactly ONE worker pool here, so
 * queueing more work never increases the number of streams, it just fills the queue.
 *
 * How wide that pool is, is NOT a setting. Nobody should have to guess a number to
 * make an upload work, and the right number is not knowable in advance: it depends on
 * the line, the moment, and who else is uploading. So it starts from what the browser
 * reports about the connection and then moves on evidence, additive-increase on a run
 * of clean files, multiplicative-decrease the moment transfers start failing. That is
 * how congestion control has worked since TCP, and for the same reason: pushing harder
 * into a saturated link makes everything slower and then makes it fail.
 *
 * The transfer itself is untouched: uploadAsset() in lib/upload.ts still does the
 * chunking, the retries with backoff, the offset re-query after a dropped connection
 * and the resume-after-reload. This file only decides what runs and when.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadAsset, StoppedError, type UploadProgress } from '@/lib/upload';
import { reportTransfer } from '@/lib/transfer-stats';
import type { AssetKind, MaterialFacets } from '@/lib/queries';
import { setUploadsBusyProbe } from '@/lib/api';

export type JobState = 'queued' | 'uploading' | 'paused' | 'done' | 'failed' | 'cancelled';

export interface UploadJob {
  id: string;
  creativeId: string;
  creativeCode: string;
  creativeTitle: string;
  kind: AssetKind;
  category?: string;
  /** set when the file goes to a clinic's raw material rather than a creative */
  material?: { clientId: string } & MaterialFacets;
  file: File;
  name: string;
  size: number;
  state: JobState;
  pct: number;
  /** bytes actually sent, which is what speed and time-left are computed from */
  loaded: number;
  /** smoothed bytes per second for THIS file, null until there is enough to say */
  bps: number | null;
  stage?: UploadProgress['stage'];
  error?: string;
  queuedAt: number;
  startedAt?: number;
  finishedAt?: number;
  /** which run of the queue this belongs to; see batchRef in the provider */
  batch: number;
  /** automatic tries after a failure on the line (Farouk 2026-09-29) */
  autoTries?: number;
  /** failed because the sign-in ran out: goes again once someone signs in */
  waitingSignIn?: boolean;
}

/** Tries a failed file gets by itself, and the wait before each. A resumable file
 *  carries on from where Google has it, so a retry costs one chunk, not the file. */
const AUTO_RETRY_WAIT_S = [5, 20, 60, 180];

/** Why a failure is worth trying again: the line, a timeout, the far end being busy.
 *  A refusal from our own API (the stage is closed, no access) will not change by
 *  itself, so it stays failed for a person to read. */
function failureKind(e: unknown): 'auth' | 'final' | 'again' {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 401) return 'auth';
  if (typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429) return 'final';
  return 'again';
}

export interface EnqueueItem {
  file: File;
  creativeId: string;
  creativeCode?: string;
  creativeTitle?: string;
  kind: AssetKind;
  category?: string;
  material?: { clientId: string } & MaterialFacets;
}

interface Ctx {
  jobs: UploadJob[];
  active: number;
  queued: number;
  paused: number;
  failed: number;
  done: number;
  /** bytes across everything still in flight or waiting */
  pendingBytes: number;
  /** 0-100 across THIS run of the queue, or null when nothing is running */
  overallPct: number | null;
  /** finished in this run of the queue, not since the browser was opened */
  doneThisRun: number;
  totalThisRun: number;
  /** bytes per second across everything moving right now, null when idle */
  bytesPerSec: number | null;
  /** seconds until the queue is empty at the current rate, null when unknowable */
  etaSeconds: number | null;
  /** how many are moving at once right now. Read only: the queue decides this. */
  limit: number;
  enqueue: (items: EnqueueItem[]) => number;
  retry: (id: string) => void;
  retryAllFailed: () => void;
  cancel: (id: string) => void;
  /** stop a file and keep what was sent; resume carries on from there */
  pause: (id: string) => void;
  resume: (id: string) => void;
  /** take a file off the list, stopping it first if it is moving */
  remove: (id: string) => void;
  pauseAll: () => void;
  resumeAll: () => void;
  cancelAll: () => void;
  clearFinished: () => void;
}

const UploadCenterContext = createContext<Ctx | null>(null);

const MIN_LIMIT = 1;
const MAX_LIMIT = 6;
/** clean files in a row before widening by one */
const WIDEN_AFTER = 3;
/** how often a transfer publishes its position, for the speed reading */
const SAMPLE_MS = 700;
/** weight of the newest sample. Lower is steadier, higher reacts faster. */
const SMOOTHING = 0.3;
/** how often the lists on screen are refreshed while files keep landing */
const REFRESH_MS = 5000;

interface NetInfo { effectiveType?: string; downlink?: number; saveData?: boolean }

/** Where to start, from what the browser will tell us about the connection.
 *  Unknown is the common case and 3 is the safe middle. */
function startingLimit(): number {
  try {
    const c = (navigator as unknown as { connection?: NetInfo }).connection;
    if (!c) return 3;
    if (c.saveData) return 1;
    const t = c.effectiveType ?? '';
    if (t.includes('2g')) return 1;
    if (t === '3g') return 2;
    if (typeof c.downlink === 'number' && c.downlink >= 20) return 4;
    return 3;
  } catch { return 3; }
}

let seq = 0;
const newId = () => `u${Date.now().toString(36)}${(seq++).toString(36)}`;

export function UploadCenterProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [limit, setLimitState] = useState<number>(startingLimit);
  const streakRef = useRef(0);
  // Which run of the queue we are in. Progress has to be measured against the work
  // in front of you, not everything the tab has ever sent: after a 111 GB day, the
  // last 75 MB is 0.07% of the total and the bar rounded to "100%" while two files
  // were still moving. A new run starts whenever the queue was idle and work arrives.
  const batchRef = useRef(0);

  // The workers read the queue through refs. Reading it from `jobs` would hand each
  // worker the snapshot it closed over and two of them would claim the same file.
  const jobsRef = useRef<UploadJob[]>([]);
  jobsRef.current = jobs;
  const limitRef = useRef(limit);
  limitRef.current = limit;
  const runningRef = useRef(0);
  const maxLanesRef = useRef(0);
  const claimedRef = useRef<Set<string>>(new Set());
  /** the stop button of every file in flight */
  const abortRef = useRef<Map<string, AbortController>>(new Map());

  const patch = useCallback((id: string, next: Partial<UploadJob>) => {
    setJobs((cur) => cur.map((j) => (j.id === id ? { ...j, ...next } : j)));
  }, []);

  const staleRef = useRef<Set<string>>(new Set());
  const staleMaterialRef = useRef<Set<string>>(new Set());
  const refreshTimerRef = useRef<number | null>(null);
  const flushRefresh = useCallback(() => {
    refreshTimerRef.current = null;
    const creatives = Array.from(staleRef.current); staleRef.current.clear();
    const clients = Array.from(staleMaterialRef.current); staleMaterialRef.current.clear();
    for (const id of creatives) {
      qc.invalidateQueries({ queryKey: ['assets', id] });
      qc.invalidateQueries({ queryKey: ['creatives', id] });
    }
    if (creatives.length) qc.invalidateQueries({ queryKey: ['creatives'] });
    for (const c of clients) qc.invalidateQueries({ queryKey: ['material', c] });
    if (clients.length) qc.invalidateQueries({ queryKey: ['material-clients'] });
  }, [qc]);
  /** Refresh at most every REFRESH_MS while files land; the last one lands within it. */
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current === null) refreshTimerRef.current = window.setTimeout(flushRefresh, REFRESH_MS);
  }, [flushRefresh]);

  const runOne = useCallback(async (job: UploadJob) => {
    const ac = new AbortController();
    abortRef.current.set(job.id, ac);
    patch(job.id, { state: 'uploading', pct: 0, loaded: 0, bps: null, error: undefined, startedAt: Date.now(), stage: 'requesting' });
    // XHR fires progress far faster than a person can read it, and every one of
    // those would be a state write and a re-render of each consumer. Publish on a
    // whole percent, a change of stage, or every SAMPLE_MS so that speed keeps
    // moving on a 2 GB file where one percent is twenty megabytes apart.
    let lastPct = -1;
    let lastStage: UploadProgress['stage'] | undefined;
    let lastAt = Date.now();
    let lastLoaded = 0;
    let bps: number | null = null;
    try {
      await uploadAsset({
        creativeId: job.creativeId,
        file: job.file,
        kind: job.kind,
        category: job.category,
        material: job.material,
        signal: ac.signal,
        onProgress: (p) => {
          const now = Date.now();
          const dt = now - lastAt;
          if (p.pct === lastPct && p.stage === lastStage && dt < SAMPLE_MS) return;
          if (dt >= SAMPLE_MS && p.loaded > lastLoaded) {
            // Exponential smoothing. A raw reading swings wildly between chunks and
            // makes the number unreadable; this settles without lagging a real stall.
            const sample = ((p.loaded - lastLoaded) * 1000) / dt;
            bps = bps === null ? sample : bps * (1 - SMOOTHING) + sample * SMOOTHING;
            lastAt = now; lastLoaded = p.loaded;
          }
          lastPct = p.pct; lastStage = p.stage;
          patch(job.id, { pct: p.pct, stage: p.stage, loaded: p.loaded, bps });
        },
      });
      patch(job.id, { state: 'done', pct: 100, loaded: job.size, bps: null, stage: 'done', finishedAt: Date.now() });
      // A run of clean files earns one more lane, slowly.
      streakRef.current += 1;
      if (streakRef.current >= WIDEN_AFTER) {
        streakRef.current = 0;
        setLimitState((n) => Math.min(MAX_LIMIT, n + 1));
      }
      // The creative the file landed on is now stale wherever it is on screen. Batched:
      // refreshing a 1,700-file list after every photo rebuilt a 2 MB answer each time
      // and froze the tab that was doing the uploading (2026-09-26).
      staleRef.current.add(job.creativeId);
      if (job.material) staleMaterialRef.current.add(job.material.clientId);
      scheduleRefresh();
    } catch (e) {
      // A person pressed pause or cancel: not a failure, and no lanes are lost over it.
      if (e instanceof StoppedError) {
        patch(job.id, e.reason === 'pause'
          ? { state: 'paused', bps: null, error: undefined }
          : { state: 'cancelled', bps: null, error: undefined, pct: 0, loaded: 0, finishedAt: Date.now() });
        return;
      }
      const msg = e instanceof Error ? e.message : 'Upload failed';
      const kind = failureKind(e);
      const tries = job.autoTries ?? 0;
      if (kind === 'auth') {
        patch(job.id, { state: 'failed', waitingSignIn: true, error: 'Signed out. It goes again as soon as you sign in.', finishedAt: Date.now() });
        return;
      }
      if (kind === 'again' && tries < AUTO_RETRY_WAIT_S.length) {
        const wait = AUTO_RETRY_WAIT_S[tries];
        patch(job.id, { state: 'failed', autoTries: tries + 1, error: `${msg}. Trying again in ${wait} s (${tries + 1} of ${AUTO_RETRY_WAIT_S.length})`, finishedAt: Date.now() });
        window.setTimeout(() => {
          setJobs((cur) => cur.map((j) => (j.id === job.id && j.state === 'failed' && j.autoTries === tries + 1
            ? { ...j, state: 'queued', error: undefined, startedAt: undefined, finishedAt: undefined } : j)));
        }, wait * 1000);
      } else {
        patch(job.id, { state: 'failed', error: msg, finishedAt: Date.now() });
      }
      // Something is not coping: the line, the browser, or the far end. Halve the
      // lanes at once rather than keep hammering, and earn them back one at a time.
      streakRef.current = 0;
      setLimitState((n) => Math.max(MIN_LIMIT, Math.floor(n / 2)));
    } finally {
      abortRef.current.delete(job.id);
      claimedRef.current.delete(job.id);
    }
  }, [patch, scheduleRefresh]);

  // Start work until the pool is full. Safe to call as often as you like: a job is
  // claimed synchronously before any await, so a second pump cannot pick it up.
  const pump = useCallback(() => {
    while (runningRef.current < limitRef.current) {
      const next = jobsRef.current.find((j) => j.state === 'queued' && !claimedRef.current.has(j.id));
      if (!next) return;
      claimedRef.current.add(next.id);
      runningRef.current += 1;
      maxLanesRef.current = Math.max(maxLanesRef.current, runningRef.current);
      void runOne(next).finally(() => {
        runningRef.current -= 1;
        pump();
      });
    }
  }, [runOne]);

  // Anything that can free or create capacity ends up here.
  useEffect(() => { pump(); }, [jobs, limit, pump]);

  const enqueue = useCallback((items: EnqueueItem[]) => {
    if (!items.length) return 0;
    const now = Date.now();
    const stillGoing = jobsRef.current.some((j) => j.state === 'queued' || j.state === 'uploading');
    if (!stillGoing) { batchRef.current += 1; maxLanesRef.current = 0; }
    const batch = batchRef.current;
    const added: UploadJob[] = items.map((it) => ({
      id: newId(),
      batch,
      creativeId: it.creativeId,
      creativeCode: it.creativeCode ?? '',
      creativeTitle: it.creativeTitle ?? '',
      kind: it.kind,
      category: it.category,
      material: it.material,
      file: it.file,
      name: it.file.name,
      size: it.file.size,
      state: 'queued',
      pct: 0,
      loaded: 0,
      bps: null,
      queuedAt: now,
    }));
    setJobs((cur) => [...cur, ...added]);
    return added.length;
  }, []);

  // Signed in again (here or in another tab): everything that stopped for it goes again.
  useEffect(() => {
    const again = () => setJobs((cur) => cur.map((j) => (j.state === 'failed' && j.waitingSignIn
      ? { ...j, state: 'queued', waitingSignIn: false, error: undefined, startedAt: undefined, finishedAt: undefined } : j)));
    window.addEventListener('kaf:signed-in', again);
    return () => window.removeEventListener('kaf:signed-in', again);
  }, []);
  // The API's 401 handler asks this before it navigates away from running uploads.
  useEffect(() => {
    setUploadsBusyProbe(() => jobsRef.current.some((j) => j.state === 'queued' || j.state === 'uploading' || (j.state === 'failed' && (j.waitingSignIn || (j.autoTries ?? 0) > 0))));
    return () => setUploadsBusyProbe(() => false);
  }, []);

  const retry = useCallback((id: string) => {
    setJobs((cur) => cur.map((j) => (
      j.id === id && (j.state === 'failed' || j.state === 'cancelled')
        ? { ...j, state: 'queued', pct: 0, error: undefined, startedAt: undefined, finishedAt: undefined, autoTries: 0, waitingSignIn: false }
        : j
    )));
  }, []);

  const retryAllFailed = useCallback(() => {
    setJobs((cur) => cur.map((j) => (
      j.state === 'failed'
        ? { ...j, state: 'queued', pct: 0, error: undefined, startedAt: undefined, finishedAt: undefined, autoTries: 0, waitingSignIn: false }
        : j
    )));
  }, []);

  // A file in flight is stopped through its signal: the chunk writer aborts its request
  // and reports back as paused or cancelled. A file not moving changes state at once.
  const stop = useCallback((ids: string[], how: 'pause' | 'cancel') => {
    const idle: string[] = [];
    for (const id of ids) {
      const ac = abortRef.current.get(id);
      if (ac) ac.abort(how); else idle.push(id);
    }
    if (!idle.length) return;
    const set = new Set(idle);
    setJobs((cur) => cur.map((j) => {
      if (!set.has(j.id)) return j;
      if (how === 'pause') return j.state === 'queued' ? { ...j, state: 'paused' } : j;
      return (j.state === 'queued' || j.state === 'paused' || j.state === 'failed')
        ? { ...j, state: 'cancelled', pct: 0, loaded: 0, finishedAt: Date.now() } : j;
    }));
  }, []);
  const cancel = useCallback((id: string) => stop([id], 'cancel'), [stop]);
  const pause = useCallback((id: string) => stop([id], 'pause'), [stop]);
  const resume = useCallback((id: string) => {
    setJobs((cur) => cur.map((j) => (j.id === id && j.state === 'paused' ? { ...j, state: 'queued', autoTries: 0 } : j)));
  }, []);
  const remove = useCallback((id: string) => {
    abortRef.current.get(id)?.abort('cancel');
    setJobs((cur) => cur.filter((j) => j.id !== id));
  }, []);
  const pauseAll = useCallback(() => {
    stop(jobsRef.current.filter((j) => j.state === 'queued' || j.state === 'uploading').map((j) => j.id), 'pause');
  }, [stop]);
  const resumeAll = useCallback(() => {
    setJobs((cur) => cur.map((j) => (j.state === 'paused' ? { ...j, state: 'queued', autoTries: 0 } : j)));
  }, []);
  const cancelAll = useCallback(() => {
    stop(jobsRef.current.filter((j) => j.state === 'queued' || j.state === 'uploading' || j.state === 'paused').map((j) => j.id), 'cancel');
  }, [stop]);

  const clearFinished = useCallback(() => {
    setJobs((cur) => cur.filter((j) => j.state === 'queued' || j.state === 'uploading'));
  }, []);

  const active = jobs.filter((j) => j.state === 'uploading').length;
  const queued = jobs.filter((j) => j.state === 'queued').length;
  const paused = jobs.filter((j) => j.state === 'paused').length;
  const failed = jobs.filter((j) => j.state === 'failed').length;
  const done = jobs.filter((j) => j.state === 'done').length;

  // When a run of the queue has settled, report how fast it really went (/transfers).
  const reportedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const batches = new Set(jobs.map((j) => j.batch));
    for (const b of batches) {
      if (reportedRef.current.has(b)) continue;
      const run = jobs.filter((j) => j.batch === b);
      if (run.some((j) => j.state === 'queued' || j.state === 'uploading' || j.state === 'paused')) continue;
      reportedRef.current.add(b);
      reportTransfer('upload', run.filter((j) => j.state === 'done'), { maxLanes: maxLanesRef.current || undefined });
    }
  }, [jobs]);

  // Leaving with transfers in flight loses the unsent part of the current file.
  // A paused file is lost too: the page holds the file itself, not a path to it.
  useEffect(() => {
    if (active + queued + paused === 0) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active, queued, paused]);

  const value = useMemo<Ctx>(() => {
    const live = jobs.filter((j) => j.state === 'queued' || j.state === 'uploading' || j.state === 'paused');
    const pendingBytes = live.reduce((n, j) => n + j.size * (1 - j.pct / 100), 0);
    const run = jobs.filter((j) => j.batch === batchRef.current && j.state !== 'cancelled');
    const totalBytes = run.reduce((n, j) => n + j.size, 0);
    const sentBytes = run.reduce((n, j) => n + j.size * (j.state === 'done' ? 1 : j.pct / 100), 0);
    // Never round up to 100 while something is still moving: a bar that says finished
    // next to two files still going is the app lying about the only thing it is for.
    const pct = totalBytes ? Math.round((sentBytes / totalBytes) * 100) : 0;

    // Speed is the sum of what the transfers in flight are each managing. Files
    // still waiting contribute nothing, which is right: they are not using the line.
    const moving = jobs.filter((j) => j.state === 'uploading' && j.bps !== null);
    const bytesPerSec = moving.length
      ? moving.reduce((n, j) => n + (j.bps ?? 0), 0)
      : null;
    // Everything still to send, at the rate the line is actually managing. Queued
    // files are included, because they are what you are waiting for.
    const etaSeconds = bytesPerSec && bytesPerSec > 0 && pendingBytes > 0
      ? Math.round(pendingBytes / bytesPerSec)
      : null;

    return {
      jobs,
      active, queued, paused, failed, done,
      pendingBytes,
      doneThisRun: run.filter((j) => j.state === 'done').length,
      totalThisRun: run.length,
      bytesPerSec,
      etaSeconds,
      overallPct: live.length ? Math.min(99, pct) : null,
      limit, enqueue, retry, retryAllFailed, cancel, pause, resume, remove, pauseAll, resumeAll, cancelAll, clearFinished,
    };
  }, [jobs, active, queued, paused, failed, done, limit, enqueue, retry, retryAllFailed, cancel, pause, resume, remove, pauseAll, resumeAll, cancelAll, clearFinished]);

  return <UploadCenterContext.Provider value={value}>{children}</UploadCenterContext.Provider>;
}

/**
 * The queue, or a dormant stand-in when there is no provider (the client portal, and
 * any test that renders a card on its own). Returning a no-op keeps every caller free
 * of "is the provider mounted" checks.
 */
export function useUploadCenter(): Ctx {
  const ctx = useContext(UploadCenterContext);
  return ctx ?? FALLBACK;
}

const FALLBACK: Ctx = {
  jobs: [], active: 0, queued: 0, paused: 0, failed: 0, done: 0, pendingBytes: 0, overallPct: null,
  doneThisRun: 0, totalThisRun: 0, bytesPerSec: null, etaSeconds: null,
  limit: 3, enqueue: () => 0, retry: () => {},
  retryAllFailed: () => {}, cancel: () => {}, pause: () => {}, resume: () => {}, remove: () => {},
  pauseAll: () => {}, resumeAll: () => {}, cancelAll: () => {}, clearFinished: () => {},
};

/** "12.4 MB/s". Per second, so it reads as a rate and not as a size. */
export function formatSpeed(bps: number | null): string {
  if (!bps || bps <= 0) return '';
  return `${formatBytes(bps)}/s`;
}

/** Time left, in the units a person would say out loud. */
export function formatEta(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 10) return 'a few seconds';
  if (seconds < 60) return `${Math.round(seconds / 5) * 5}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const rest = Math.round((seconds % 60) / 15) * 15;
    return rest && rest < 60 ? `${m}m ${rest}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function formatBytes(n: number): string {
  if (!n || n < 1) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  // Clamp BOTH ends. A part-sent file leaves a fraction of a byte outstanding, and
  // log(0.23)/log(1024) floors to -1, which indexed off the front of the array and
  // printed "235.5 undefined left to send".
  const i = Math.max(0, Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024))));
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}
